import {
  FloatingFormItem,
  InputDecimal,
  InputText,
} from '@/components/_common';
import { useEffect } from 'react';
import { Button, Col, Form, Row } from 'antd';
import { useIntl } from 'react-intl';
import {
  useBillingInvoice,
  useGetBillingInvoiceId,
  useUpdateBillingInvoice,
} from '@/core/domain/invoice/Billing/Billing.hook';
import { useRouteMatch } from 'react-router-dom';
import {
  BillingCreateRequest,
  BillingUpdateRequest,
} from '@/core/domain/invoice/Billing/Billing.types';

interface Props {
  disabledFields?: boolean;
}

const InvoicePaymentConditionBillingData = ({ disabledFields }: Props) => {
  const intl = useIntl().formatMessage;
  const translate = (message: string) =>
    intl({ id: `invoicePaymentCondition.label.${message}` });

  const [form] = Form.useForm<BillingCreateRequest>();

  const discount = Form.useWatch('discount', form);
  const originalValue = Form.useWatch('originalValue', form);

  const { invoiceId } = useRouteMatch<{ invoiceId: string }>().params;

  const { mutateAsync: createMutateAsync, isLoading: isLoadingCreate } =
    useBillingInvoice();
  const { mutateAsync: updateMutateAsync, isLoading: isLoadingUpdate } =
    useUpdateBillingInvoice();
  const { mutateAsync: getMutateAsync, data } = useGetBillingInvoiceId();

  useEffect(() => {
    if (invoiceId) getMutateAsync(Number(invoiceId));
  }, [invoiceId]);

  useEffect(() => {
    if (data) form.setFieldsValue({ ...data });
  }, [data]);

  useEffect(() => {
    if (discount || originalValue)
      form.setFieldValue('netValue', originalValue - discount || 0);
  }, [discount, originalValue]);

  const handleOk = async () => {
    try {
      const formValues = await form.validateFields();

      if (data?.id) {
        const objUpdate: BillingUpdateRequest = {
          ...formValues,
          number: formValues.number || undefined,
          id: data.id,
          invoiceId: Number(invoiceId),
        };

        await updateMutateAsync(objUpdate);
      } else {
        const objCreate: BillingCreateRequest = {
          ...formValues,
          number: formValues.number || undefined,
          invoiceId: Number(invoiceId),
        };

        await createMutateAsync(objCreate);
        getMutateAsync(Number(invoiceId));
      }
    } catch (err) {}
  };

  return (
    <Form form={form}>
      <Row gutter={[16, 16]} justify="end">
        <Col>
          <Button
            type="primary"
            onClick={() => handleOk()}
            disabled={disabledFields || isLoadingUpdate || isLoadingCreate}
          >
            Salvar
          </Button>
        </Col>

        <Col span={24}>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12} xl={5}>
              <FloatingFormItem
                name="number"
                label={translate('invoiceNumber')}
              >
                <InputText onlyNumber disabled={disabledFields} />
              </FloatingFormItem>
            </Col>

            <Col xs={24} md={12} xl={5}>
              <FloatingFormItem
                name="originalValue"
                label={translate('originalValue')}
                initialValue={0}
              >
                <InputDecimal disabled={disabledFields} />
              </FloatingFormItem>
            </Col>

            <Col xs={24} md={12} xl={5}>
              <FloatingFormItem
                name="discount"
                label={translate('discountValue')}
                initialValue={0}
              >
                <InputDecimal disabled={disabledFields} max={originalValue} />
              </FloatingFormItem>
            </Col>

            <Col xs={24} md={12} xl={5}>
              <FloatingFormItem
                name="netValue"
                label={translate('netValue')}
                initialValue={0}
              >
                <InputDecimal disabled={disabledFields} />
              </FloatingFormItem>
            </Col>

            <Col xs={24} md={12} xl={4}>
              <FloatingFormItem
                name="changeValue"
                label="Valor troco"
                initialValue={0}
              >
                <InputDecimal disabled={disabledFields} />
              </FloatingFormItem>
            </Col>
          </Row>
        </Col>
      </Row>
    </Form>
  );
};

export default InvoicePaymentConditionBillingData;
